import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatDistanceToNow } from "date-fns";
import { getNotificationBadge } from "../../lib/notificationTypes";
import { playLeadChime } from "../../lib/chime";

const CHIME_TYPES = new Set(["datamoon_lead_captured", "trulli_deal_update", "rep_low_leads"]);
const CHIME_LABELS = { datamoon_lead_captured: "New lead captured", trulli_deal_update: "Deal update", rep_low_leads: "Low lead alert" };

export function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get("/admin/notifications", { params: { limit: 8 } });
      if (!firstLoadRef.current) {
        const newAlerts = data.notifications.filter((n) => CHIME_TYPES.has(n.type) && !seenIdsRef.current.has(n.id));
        if (newAlerts.length > 0) {
          playLeadChime();
          toast.success(`${CHIME_LABELS[newAlerts[0].type]}: ${newAlerts[0].message}`, { duration: 6000 });
        }
      }
      seenIdsRef.current = new Set(data.notifications.map((n) => n.id));
      firstLoadRef.current = false;
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markAllRead = async () => {
    await api.patch("/admin/notifications/read-all");
    fetchNotifications();
  };

  const markOneRead = async (id, read) => {
    if (read) return;
    try {
      await api.patch(`/admin/notifications/${id}/read`);
      fetchNotifications();
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell-button"
        onClick={() => setOpen(!open)}
        title="Opens your notifications — new leads, deal updates, and low-lead alerts."
        className="relative p-2 text-igc-cream hover:text-igc-goldText transition-colors"
      >
        <Bell size={22} weight={unreadCount > 0 ? "fill" : "duotone"} />
        {unreadCount > 0 && (
          <span data-testid="notification-unread-count" className="absolute -top-1 -right-1 bg-igc-gold text-black text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-dropdown" className="absolute right-0 mt-3 w-96 bg-igc-surface border border-igc-gold/20 shadow-2xl z-50 max-h-[28rem] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-igc-gold/10">
            <p className="font-serif text-igc-cream">Notifications</p>
            <button data-testid="mark-all-read-button" onClick={markAllRead} title="Marks every notification here as read." className="text-xs text-igc-goldText hover:text-igc-goldHover">Mark all read</button>
          </div>
          {notifications.length === 0 && <p className="p-6 text-igc-muted text-sm text-center" data-testid="notification-dropdown-empty">{loading ? "Loading..." : "No notifications yet."}</p>}
          {notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => markOneRead(n.id, n.read)}
              data-testid={`notification-item-${n.id}`}
              title={n.read ? "" : "Click to mark this notification as read"}
              className={`w-full text-left p-4 border-b border-igc-gold/5 border-l-2 ${n.read ? "border-l-transparent" : "border-l-igc-gold"}`}
            >
              <span data-testid={`notification-bell-type-badge-${n.id}`} className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border mb-1.5 ${getNotificationBadge(n.type).cls}`}>
                {getNotificationBadge(n.type).label}
              </span>
              <p className="text-igc-cream text-sm">{n.message}</p>
              <p className="text-igc-muted text-xs mt-1">{formatDistanceToNow(new Date(n.timestamp), { addSuffix: true })}</p>
            </button>
          ))}
          <Link
            to="/admin/notifications"
            data-testid="view-all-notifications-link"
            onClick={() => setOpen(false)}
            title="Opens the full Notification Center with every notification ever sent."
            className="block p-3 text-center text-xs text-igc-goldText hover:text-igc-goldHover border-t border-igc-gold/10"
          >
            View All in Notification Center →
          </Link>
        </div>
      )}
    </div>
  );
}
